import React, { useEffect, useState } from 'react';
import {
  sampleService,
  type SampleSet,
  type SampleItem,
  type ProcedureInstance,
} from '../../../services/auditService';
import Button from '../../common/Button';
import type { ResponseRole } from './responseShellTypes';

/**
 * SamplePickerGate — Phase-106 revised model (sample-reconcile).
 *
 * Sampling now happens ONCE in the Program Lead "Scope" step
 * (POST /scope spawns each task with its own 1-item ADT_sample_sets row).
 * By the time a performer opens a response, the active set already exists.
 *
 * Behavior:
 *   1. Fetch the procedure_instance's ACTIVE ADT_sample_sets row.
 *      - Found? Fetch its items and render `children(set, items)`.
 *      - Not found? Render a friendly "not scoped yet" read-only message.
 *        The Program Lead must scope the procedure from the Program Lead portal
 *        before responses can be recorded.
 *
 * Removed (Phase-106):
 *   - SamplePickerStep (in-execution Geographies picker) — sampling is
 *     done by the Program Lead, not the performer at response time.
 *   - "Manage Samples" bar + DiscardSamplesConfirmDialog — samples are
 *     immutable once scoped; re-scoping goes through /scope again.
 *   - spawn/picker-saved/cancel flow (sampleService.spawnSampleInstances).
 *
 * Props contract is unchanged so ChecklistResponse / ConfirmationResponse /
 * FormResponse / PhysicalCheckResponse compile without modification.
 */

export type SamplePickerGateChildren = (
  sampleSet: SampleSet,
  sampleItems: SampleItem[],
) => React.ReactNode;

interface Props {
  instance: ProcedureInstance;
  /** Used for type-dispatch in the parent response component. */
  procedureType: 'Checklist' | 'Form' | 'Confirmation' | 'PhysicalCheck';
  children: SamplePickerGateChildren;
  /** Phase 113 — role of the current user; enables Roll Out Work button for performer/lead. */
  role?: ResponseRole;
  /** Phase 113 — called when the Roll Out Work button is clicked. */
  onRollOut?: () => void;
  /** Phase 113 — post-approval lock. */
  readOnly?: boolean;
}

const SamplePickerGate: React.FC<Props> = ({ instance, children, role, onRollOut, readOnly = false }) => {
  const [activeSet, setActiveSet] = useState<SampleSet | null>(null);
  const [items, setItems] = useState<SampleItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Resolve active sample set + its items.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const sets = await sampleService.listSets(instance.id);
        const active = sets.find(s => (s.is_active ?? 1) === 1) ?? null;
        if (!active) {
          if (!cancelled) {
            setActiveSet(null);
            setItems([]);
          }
          return;
        }
        const its = await sampleService.listItems(active.id);
        if (!cancelled) {
          setActiveSet(active);
          setItems(its);
        }
      } catch (e: any) {
        if (!cancelled) {
          setError(
            e?.response?.data?.detail ??
              e?.message ??
              'Failed to load samples.',
          );
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [instance.id]);

  if (loading) {
    return <div className="response-shell-body modal-empty-text">Loading samples…</div>;
  }

  if (error) {
    return <div role="alert" className="inline-error">{error}</div>;
  }

  // No active set — procedure hasn't been scoped / rolled out yet.
  if (!activeSet) {
    const canRollOut = (role === 'performer' || role === 'lead') && !readOnly;
    return (
      <div className="response-shell-body modal-empty-text">
        {canRollOut ? (
          <Button variant="fill" onClick={onRollOut} data-testid="BTN_roll_out_work">
            Roll Out Work
          </Button>
        ) : (
          <>This procedure hasn&apos;t been scoped yet. Your Program Lead scopes it
          (Population + samples) before responses can be recorded.</>
        )}
      </div>
    );
  }

  // Active set exists — render the body via the render prop.
  return <>{children(activeSet, items)}</>;
};

export default SamplePickerGate;
